import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import ListLoader from "../../common-components/ListLoader";
import { ToastContainer, toast } from "react-toastify";
import useGetPhrAppDepartments from "../doctor-information-management/hooks/useGetPhrAppDepartments";
import useGetDoctorsByDepartment from "../doctor-information-management/hooks/useGetDoctorsByDepartment";
import useGetDoctorAvailableDates from "./hooks/useGetDoctorAvailableDates";
import useGetDoctorSlots from "./hooks/useGetDoctorSlots";
import useReserveUnreserveSlot from "./hooks/useReserveUnreserveSlot";
import Modal from "../../common-components/Modal";
import TertiaryButton from "../../common-components/Buttons/TertiaryButton";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";

const HMISDoctorSlotManagement = () => {
  const { t } = useTranslation();
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [noOfDays, setNoOfDays] = useState(10);
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const { allDepartments, isDepartmentLoading } = useGetPhrAppDepartments();
  const { doctors, isDoctorsLoading, getDoctorsByDepartment } = useGetDoctorsByDepartment();
  const { availableDates, isLoading: isLoadingDates, getAvailableDates } = useGetDoctorAvailableDates();
  const { slots, isLoading: isLoadingSlots, getDoctorSlots } = useGetDoctorSlots();
  const { reserveSlot, unreserveSlot, bulkReserveSlots, isLoading: isReserving } = useReserveUnreserveSlot();

  // Filter active departments
  const activeDepartments = useMemo(() => {
    if (!Array.isArray(allDepartments)) {
      return [];
    }
    return allDepartments.filter(dept => dept.isActive && !dept.isDelete);
  }, [allDepartments]);

  // Set first department as selected when departments are loaded
  useEffect(() => {
    if (activeDepartments.length > 0 && !selectedDepartment) {
      const firstDept = activeDepartments[0];
      setSelectedDepartment(firstDept.name || firstDept.departmentName || firstDept.deptname);
    }
  }, [activeDepartments, selectedDepartment]);

  // Fetch doctors when department is selected
  useEffect(() => {
    if (selectedDepartment) {
      getDoctorsByDepartment(selectedDepartment);
    }
  }, [selectedDepartment, getDoctorsByDepartment]);

  // Fetch available dates when doctor is selected or noOfDays changes
  useEffect(() => {
    if (selectedDoctor && selectedDepartment) {
      const doctorId = selectedDoctor.docId || selectedDoctor.static_data?.docId;
      // Find department ID from activeDepartments
      const department = activeDepartments.find(
        dept => (dept.name || dept.departmentName || dept.deptname) === selectedDepartment
      );
      const departmentId = department?.id || department?.deptId;
      
      if (doctorId && departmentId) {
        getAvailableDates(doctorId, departmentId, noOfDays);
      }
    }
  }, [selectedDoctor, selectedDepartment, activeDepartments, noOfDays, getAvailableDates]);

  // Auto-select first date when available dates are loaded (only if not loading)
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate && selectedDoctor && !isLoadingDates) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDoctor, selectedDate, isLoadingDates]);

  // Fetch slots when date is selected (only if dates are not loading)
  useEffect(() => {
    if (selectedDoctor && selectedDate && selectedDepartment && !isLoadingDates) {
      const doctorId = selectedDoctor.docId || selectedDoctor.static_data?.docId;
      // Find department ID from activeDepartments
      const department = activeDepartments.find(
        dept => (dept.name || dept.departmentName || dept.deptname) === selectedDepartment
      );
      const departmentId = department?.id || department?.deptId;
      
      if (doctorId && departmentId) {
        getDoctorSlots(doctorId, departmentId, selectedDate);
      }
    }
  }, [selectedDoctor, selectedDate, selectedDepartment, activeDepartments, isLoadingDates, getDoctorSlots]);

  const handleDepartmentSelect = (departmentName) => {
    setSelectedDepartment(departmentName);
    setSelectedDoctor(null);
    setSelectedDate(null);
    setIsSlotModalOpen(false);
  };

  const handleDoctorClick = (doctor) => {
    setSelectedDoctor(doctor);
    setSelectedDate(null); // Reset date, will be auto-selected when dates load
    setIsSlotModalOpen(true);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlots(new Set());
    setIsSelectionMode(false);
  };

  const handleReserveUnreserve = async (slot) => {
    const doctorId = selectedDoctor?.docId || selectedDoctor?.static_data?.docId;
    if (!doctorId || !selectedDepartment) return;

    // Find department ID from activeDepartments
    const department = activeDepartments.find(
      dept => (dept.name || dept.departmentName || dept.deptname) === selectedDepartment
    );
    const departmentId = department?.id || department?.deptId;
    if (!departmentId) return;

    // Use eventStartDateTime as the event_start_time parameter (required by API)
    const eventStartTime = slot.eventStartDateTime;
    if (!eventStartTime) {
      toast.error("Slot event start time is missing");
      return;
    }
    
    if (slot.reserved) {
      const success = await unreserveSlot(doctorId, eventStartTime);
      if (success && selectedDate) {
        // Refresh slots after unreserving
        getDoctorSlots(doctorId, departmentId, selectedDate);
      }
    } else {
      const success = await reserveSlot(doctorId, eventStartTime);
      if (success && selectedDate) {
        // Refresh slots after reserving
        getDoctorSlots(doctorId, departmentId, selectedDate);
      }
    }
  };

  const handleSlotToggle = (eventStartTime) => {
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(eventStartTime)) {
      newSelected.delete(eventStartTime);
    } else {
      newSelected.add(eventStartTime);
    }
    setSelectedSlots(newSelected);
  };

  const handleSelectAllForDay = () => {
    const unreservedSlots = slots
      .filter(slot => !slot.reserved && slot.eventStartDateTime)
      .map(slot => slot.eventStartDateTime);
    setSelectedSlots(new Set(unreservedSlots));
  };

  const handleDeselectAll = () => {
    setSelectedSlots(new Set());
  };

  const handleBulkReserve = async () => {
    if (selectedSlots.size === 0) {
      toast.error("Please select at least one slot to reserve");
      return;
    }

    const doctorId = selectedDoctor?.docId || selectedDoctor?.static_data?.docId;
    if (!doctorId || !selectedDepartment) {
      toast.error("Doctor or department information is missing");
      return;
    }

    // Find department ID from activeDepartments
    const department = activeDepartments.find(
      dept => (dept.name || dept.departmentName || dept.deptname) === selectedDepartment
    );
    const departmentId = department?.id || department?.deptId;
    if (!departmentId) {
      toast.error("Department ID not found");
      return;
    }

    // Filter out any invalid eventStartTime values
    const eventStartTimes = Array.from(selectedSlots).filter(time => time && typeof time === 'string' && time.trim());
    
    if (eventStartTimes.length === 0) {
      toast.error("No valid slot times selected");
      return;
    }
    
    console.log("Bulk reserve request:", {
      doctorId,
      eventStartTimes,
      count: eventStartTimes.length
    });
    
    const success = await bulkReserveSlots(doctorId, eventStartTimes);
    if (success && selectedDate) {
      // Refresh slots after bulk reserving
      getDoctorSlots(doctorId, departmentId, selectedDate);
      setSelectedSlots(new Set());
      setIsSelectionMode(false);
    }
  };

  // Department Tabs Component
  const DepartmentTabs = () => {
    if (isDepartmentLoading) {
      return (
        <div className="flex justify-center py-4">
          <ListLoader />
        </div>
      );
    }

    if (activeDepartments.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          {t("No departments found")}
        </div>
      );
    }

    return (
      <div className="flex gap-2 overflow-x-auto pb-2 border-b border-gray-200">
        {activeDepartments.map((dept) => {
          const deptName = dept.name || dept.departmentName || dept.deptname || "";
          const isSelected = selectedDepartment === deptName;
          return (
            <button
              key={dept.id || dept.deptId || deptName}
              onClick={() => handleDepartmentSelect(deptName)}
              className={`px-4 py-2 rounded-t-lg font-medium whitespace-nowrap transition-colors ${
                isSelected
                  ? "bg-[#4C6AF7] text-white border-b-2 border-[#4C6AF7]"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {deptName}
            </button>
          );
        })}
      </div>
    );
  };

  // Doctor Card Component
  const DoctorCard = ({ doctor }) => {
    const doctorData = doctor.static_data || doctor;
    const doctorName = doctorData.doctor_name || doctor.doctor || doctorData.doctor || "--";
    const photoUrl = doctorData.photo_url || doctor.doctorImage || "--";

    return (
      <div
        className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 overflow-hidden"
        onClick={() => handleDoctorClick(doctor)}
      >
        <div className="p-4">
          {/* Doctor Image */}
          <div className="flex justify-center mb-3">
            {photoUrl && photoUrl !== "--" ? (
              <img
                src={photoUrl}
                alt={doctorName}
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/96?text=No+Image";
                }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Doctor Name */}
          <h3 className="text-lg font-semibold text-center mb-3 text-gray-800 truncate">
            {doctorName}
          </h3>

          <div className="text-center">
            <span className="text-sm text-gray-500">{t("Click to manage slots")}</span>
          </div>
        </div>
      </div>
    );
  };

  // Doctors Grid Component
  const DoctorsGrid = () => {
    if (isDoctorsLoading) {
      return (
        <div className="flex justify-center py-12">
          <ListLoader />
        </div>
      );
    }

    if (doctors.length === 0) {
      return (
        <div className="p-8 text-center text-gray-500">
          {t("No doctors found in this department")}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-6">
        {doctors.map((doctor, index) => {
          const doctorData = doctor.static_data || doctor;
          const key = doctorData.docId || doctor.docId || doctor.id || index;
          return <DoctorCard key={key} doctor={doctor} />;
        })}
      </div>
    );
  };

  // Slot Management Modal
  const SlotManagementModal = () => {
    const doctorName = selectedDoctor
      ? (selectedDoctor.static_data?.doctor_name || selectedDoctor.doctor || selectedDoctor.static_data?.doctor || "--")
      : "--";

    return (
      <Modal
        showModal={isSlotModalOpen}
        onClose={() => {
          setIsSlotModalOpen(false);
          setSelectedDoctor(null);
          setSelectedDate(null);
          setSelectedSlots(new Set());
          setIsSelectionMode(false);
        }}
        modalHeight="h-[90%]"
        modalWidth="w-[90%]"
      >
        <div className="p-5 overflow-y-auto">
          <h2 className="text-xl font-semibold mb-4">
            {t("Manage Slots for")} {doctorName}
          </h2>

          {/* Available Dates Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">{t("Available Dates")}</h3>
              <button
                onClick={() => {
                  setNoOfDays(prev => prev + 10);
                }}
                className="px-4 py-2 bg-[#4C6AF7] text-white rounded-md hover:bg-[#3d5ae5] text-sm font-medium transition-colors"
              >
                {t("Show More")} (+10 {t("days")})
              </button>
            </div>
            {isLoadingDates ? (
              <ListLoader />
            ) : availableDates.length === 0 ? (
              <p className="text-gray-500">{t("No available dates found")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableDates.map((date) => {
                  // Parse DD/MM/YYYY format
                  const parseDDMMYYYY = (dateStr) => {
                    if (!dateStr) return null;
                    // Try DD/MM/YYYY format first
                    const parts = dateStr.split('/');
                    if (parts.length === 3) {
                      const day = parseInt(parts[0], 10);
                      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
                      const year = parseInt(parts[2], 10);
                      if (!isNaN(day) && !isNaN(month) && !isNaN(year) && day > 0 && month >= 0 && year > 0) {
                        const dateObj = new Date(year, month, day);
                        // Verify the date is valid (check if it matches the input)
                        if (dateObj.getDate() === day && dateObj.getMonth() === month && dateObj.getFullYear() === year) {
                          return dateObj;
                        }
                      }
                    }
                    // Fallback: try standard Date parsing
                    const fallbackDate = new Date(dateStr);
                    if (!isNaN(fallbackDate.getTime())) {
                      return fallbackDate;
                    }
                    return null;
                  };

                  const dateObj = parseDDMMYYYY(date);
                  const isValidDate = dateObj !== null;
                  const displayDate = isValidDate ? dateObj.toLocaleDateString() : date;
                  
                  return (
                    <button
                      key={date}
                      onClick={() => handleDateSelect(date)}
                      className={`px-4 py-2 rounded-md font-medium transition-colors ${
                        selectedDate === date
                          ? "bg-[#4C6AF7] text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {displayDate}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Slots Section */}
          {selectedDate && !isLoadingDates && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium">
                  {t("Slots for")} {(() => {
                    const parts = selectedDate.split('/');
                    if (parts.length === 3) {
                      const dateObj = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                      return dateObj.toLocaleDateString();
                    }
                    return selectedDate;
                  })()}
                </h3>
                {!isLoadingSlots && slots.length > 0 && (
                  <div className="flex gap-2">
                    {!isSelectionMode ? (
                      <button
                        onClick={() => setIsSelectionMode(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                      >
                        {t("Select Slots")}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleSelectAllForDay}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs font-medium transition-colors"
                        >
                          {t("Select All")}
                        </button>
                        <button
                          onClick={handleDeselectAll}
                          className="px-3 py-1.5 bg-gray-400 text-white rounded-md hover:bg-gray-500 text-xs font-medium transition-colors"
                        >
                          {t("Deselect All")}
                        </button>
                        <button
                          onClick={() => {
                            setIsSelectionMode(false);
                            setSelectedSlots(new Set());
                          }}
                          className="px-3 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-xs font-medium transition-colors"
                        >
                          {t("Cancel")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {isLoadingSlots ? (
                <ListLoader />
              ) : slots.length === 0 ? (
                <p className="text-gray-500">{t("No slots found for this date")}</p>
              ) : (
                <>
                  {isSelectionMode && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          {selectedSlots.size} {t("slot(s) selected")}
                        </p>
                        <button
                          onClick={handleBulkReserve}
                          disabled={isReserving || selectedSlots.size === 0}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isReserving ? t("Processing...") : t("Bulk Reserve Selected")}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {slots.map((slot, index) => {
                      const isSelected = selectedSlots.has(slot.eventStartDateTime);
                      const isUnreserved = !slot.reserved;
                      const canSelect = isSelectionMode && isUnreserved;
                      
                      return (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border-2 ${
                            slot.reserved
                              ? "border-red-300 bg-red-50"
                              : isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-green-300 bg-green-50"
                          }`}
                        >
                          {isSelectionMode && canSelect && (
                            <div className="mb-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSlotToggle(slot.eventStartDateTime)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                            </div>
                          )}
                          <div className="mb-2">
                            <p className="font-semibold text-gray-800">
                              {slot.slotTimeStr || slot.eventStartDateTime || "--"}
                            </p>
                            {slot.eventStartDateTime && slot.eventEndDateTime && (
                              <p className="text-sm text-gray-600">
                                {new Date(slot.eventStartDateTime).toLocaleTimeString()} -{" "}
                                {new Date(slot.eventEndDateTime).toLocaleTimeString()}
                              </p>
                            )}
                            {slot.isVideoConsultation && (
                              <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                {t("Video")}
                              </span>
                            )}
                          </div>
                          {!isSelectionMode && (
                            <button
                              onClick={() => handleReserveUnreserve(slot)}
                              disabled={isReserving}
                              className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                slot.reserved
                                  ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-green-600 text-white hover:bg-green-700"
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {isReserving
                                ? t("Processing...")
                                : slot.reserved
                                ? t("Unreserve")
                                : t("Reserve")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 mt-6 border-t">
            <TertiaryButton
              buttonName={t("Close")}
              onClick={() => {
                setIsSlotModalOpen(false);
                setSelectedDoctor(null);
                setSelectedDate(null);
                setSelectedSlots(new Set());
                setIsSelectionMode(false);
              }}
            />
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className="m-5 text-sm text-primary">
      <ToastContainer />
      <div className="my-5">
        {/* Department Tabs */}
        <div className="mb-6">
          <DepartmentTabs />
        </div>

        {/* Doctors Grid */}
        <div className="bg-gray-50 rounded-lg">
          <DoctorsGrid />
        </div>
      </div>

      <SlotManagementModal />
    </div>
  );
};

export default HMISDoctorSlotManagement;
